import type {ElementContent, Root as HtmlRoot} from 'hast'
import type {Delimiter, DelimiterParents, HtmlData, Nodes as MarkdownNodes, Root as MarkdownRoot} from 'mdast'
import {type Handler, type Raw, toHast} from 'mdast-util-to-hast'

import {setOfAll} from '../utils'
import {compareNodePositions} from './positioned-node'

// Declare that delimiters can be passed through into the hast tree from mdast
declare module 'hast' {
  export interface DelimiterContentMap {
    delimiter: Delimiter
  }

  export type DelimiterContent = DelimiterContentMap[keyof DelimiterContentMap]

  interface RootContentMap extends DelimiterContentMap {}

  interface ElementContentMap extends DelimiterContentMap {}

  interface Text {
    /** Even text nodes can contain delimiters, as in the case of backslash-escaped characters. */
    children?: DelimiterContent[]
  }
}

declare module 'mdast-util-to-hast' {
  interface RawData extends HtmlData {}
}

function getDelimiterChildren(node: MarkdownNodes): Delimiter[] {
  // For some reason a simple `filter` call with a type guard isn't satisfying the typechecker here, but this is
  const result: Delimiter[] = []
  for (const child of node.children ?? []) if (child.type === 'delimiter') result.push(child)
  return result
}

/**
 * Nodes that aren't normally expected to have children (like thematic breaks) won't get their children processed by
 * hast, even if those children are passthrough nodes. So we have to implement a custom handler that preserves the
 * delimiter children ourselves.
 */
const handleDelimiterParentNode: Handler = (state, node: MarkdownNodes) => {
  const result = toHast(node) as ElementContent
  if (result.type === 'element' || result.type === 'text') {
    if (result.children?.length) {
      result.children = [...result.children, ...getDelimiterChildren(node)].sort(compareNodePositions)
    } else {
      // If there's no existing children, just set the already-sorted delimiters as the children
      result.children = getDelimiterChildren(node)
    }
  }
  return result
}

type DelimiterParentType = Exclude<
  DelimiterParents['type'],
  // We actually do want to handle displaymath and inlinemath eventually, but we don't currently support math at all so those
  // node types don't exist unless we add the markdown-math extension, which we aren't ready to do yet. The problem is
  // that the types will still _sometimes_ exist, if consumer depends on both markdown-math and hybrid-markdown-editor,
  // so we have to exclude them for now.
  'delimiter' | 'html' | 'raw' | 'displaymath' | 'inlinemath'
>

const delimiterParentTypes = setOfAll<DelimiterParentType>()(
  'break',
  'code',
  'definition',
  'footnoteReference',
  'image',
  'imageReference',
  'inlineCode',
  'text',
  'thematicBreak',
  'yaml',
)

const delimiterParentHandlers = Object.fromEntries(
  Array.from(delimiterParentTypes).map(t => [t, handleDelimiterParentNode]),
)

/** Handle HTML nodes by copying over the data from our `enrich-html` extension. */
const handleHtmlNode: Handler = (state, node: MarkdownNodes) => {
  const raw = toHast(node, {allowDangerousHtml: true}) as Raw
  raw.data ??= {}
  Object.assign(raw.data, node.data)
  return raw
}

/**
 * Convert the Markdown (mdast) AST to an HTML (hast) AST, passing `delimiter` nodes through unchanged.
 */
export function buildHtmlTree(markdownTree: MarkdownRoot): HtmlRoot {
  return toHast(markdownTree, {
    passThrough: ['delimiter'],
    handlers: {
      ...delimiterParentHandlers,
      html: handleHtmlNode,
    },
    allowDangerousHtml: true,
  }) as HtmlRoot // we know since we passed an mdast `root`, we'll get a hast `root` as the result
}
